<template>
  <mp-alert-error v-if="errors.length > 0" :errors="errors">
    There was one or more errors loading the server.
  </mp-alert-error>
  <mp-loading-block v-else-if="loading">
    Loading Server
  </mp-loading-block>
  <div v-else>
    <mp-form-text
      ref="name"
      v-model="attributes.name"
      :required="true"
      label="Name"
      description="The alias of the job"
    />
    <mp-form-toggle
      ref="enabled"
      v-model="attributes.enabled"
      type="switch"
      label="Enabled"
      description="If this server should be enabled"
    />
    <mp-form-toggle
        ref="syncEnabled"
        v-model="attributes.syncEnabled"
        type="switch"
        label="Sync Enabled"
        description="If this server should sync job recordings"
    />

    <template v-if="attributes.type === 'BLOCK'">
      <mp-form-select
        ref="blockLength"
        v-model="attributes.blockLength"
        label="Block Length"
        :options="blockLengthOptions"
        description="The Length of the recording block in hours"
      />
    </template>
    <template v-else-if="attributes.type === 'CUSTOM'">
      <mp-form-select
        ref="dayOfTheWeek"
        v-model="attributes.dayOfTheWeek"
        label="Day of the Week"
        :options="dayOptions"
        description="The day of the week that this job should run on"
      />
      <mp-form-number
        ref="startTime"
        v-model="attributes.startTime"
        :precision="0"
        label="Start Time"
        description="The time the recording should start (local to the station)"
      />
      <mp-form-number
        ref="finishTime"
        v-model="attributes.finishTime"
        :precision="0"
        label="Finish Time"
        description="The time the recording should finish (local to the station)"
      />
    </template>
    <mp-alert-error v-if="formErrors.length > 0" :errors="formErrors">
      There was one or more errors saving the server.
    </mp-alert-error>
    <div class="buttons">
      <mp-btn type="reset" display-type="light" @click="$emit('close')">
        Cancel
      </mp-btn>
      <mp-btn
        type="submit"
        display-type="primary"
        :loading="saving"
        @click="save"
      >
        Save
      </mp-btn>
    </div>
  </div>
</template>

<script>
import { JobManager } from "../../Manager/JobManager";
import { API } from "@memberpoint/mp-frontend-core";
import FormValidation from "@/VueResources/Mixins/FormValidation";

export default {
  name: "EditJobForm",
  mixins: [FormValidation],
  props: {
    id: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      errors: [],
      loading: false,
      saving: false,
      job: null,
      attributes: {
        name: null,
        type: null,
        blocKLength: null,
        enabled: null,
        syncEnabled: null,
        dayOfTheWeek: null,
        startTime: null,
        finishTime: null
      },
      blockLengthOptions: {
        "4": "4",
        "2": "2",
        "6": "6"
      },
      dayOptions: {
        1: "Monday",
        2: "Tuesday",
        3: "Wednesday",
        4: "Thursday",
        5: "Friday",
        6: "Saturday",
        7: "Sunday"
      },
      formErrors: []
    };
  },
  watch: {
    id: {
      immediate: true,
      handler(nv) {
        if (nv !== null) {
          this.load();
        }
      }
    }
  },
  methods: {
    /**
     * Load up the job
     */
    load() {
      this.loading = true;
      let manager = new JobManager();
      manager
        .getJobById(this.id)
        .then(apiResponse => {
          this.job = apiResponse.getSingleData();
          this.attributes.name = this.job.get("name");
          this.attributes.type = this.job.get("type");
          this.attributes.enabled = this.job.getBool("isEnabled");
          this.attributes.syncEnabled = this.job.getBool("isSyncEnabled");

          this.attributes.blockLength = this.job.get("block_length", null);
          this.attributes.startTime = this.job.get("start_time", null);
          this.attributes.finishTime = this.job.get("finish_time", null);
          this.attributes.dayOfTheWeek = this.job.get("day_of_the_week", null);
        })
        .catch(err => {
          this.errors = API.SimpleAPIErrorParser.parse(err);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    isValid() {
      if (this.attributes.type === "BLOCK") {
        return this.refsAreValid("name", "enabled", "syncEnabled", "blockLength");
      } else if (this.attributes.type === "CUSTOM") {
        return this.refsAreValid(
          "name",
          "enabled",
          "syncEnabled",
          "startTime",
          "finishTime",
          "dayOfTheWeek"
        );
      }
      return false;
    },
    getUpdateAttributes() {
      let attributes = {
        isEnabled: this.attributes.enabled,
        isSyncEnabled: this.attributes.syncEnabled,
        name: this.attributes.name
      };
      if (this.attributes.type === "BLOCK") {
        attributes["blockLength"] = this.attributes.blockLength;
      }
      if (this.attributes.type === "CUSTOM") {
        attributes["dayOfTheWeek"] = this.attributes.dayOfTheWeek;
        attributes["startTime"] = this.attributes.startTime;
        attributes["finishTime"] = this.attributes.finishTime;

      }
      return attributes;
    },
    save() {
      if (this.isValid()) {
        this.saving = true;
        let manager = new JobManager();
        manager
          .updateJob(this.id, this.getUpdateAttributes())
          .then(apiResponse => {
            this.$emit("input");
          })
          .catch(err => {
            this.processFormValidationErrors(
              err,
              {
                name: "name",
                blockLength: "blockLength",
                enabled: "isEnabled"
              },
              "formErrors"
            );
          })
          .finally(() => {
            this.saving = false;
          });
      }
    }
  }
};
</script>

<style scoped></style>
